import { CHAT_LIMITS, type ChatMessageView, type MatchChatView } from '@mogzo/shared';
import { CircleAlert, Flag, SendHorizontal } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useSession } from '../../lib/session';
import { Avatar } from '../community/Avatar';

/**
 * A match's chat beside the game on a computer (M38b), in the panel look: its messages, the
 * quick phrases and, if the mode allows typing, a box to write in.
 */
export function MatchChatSide({
  view,
  send,
  onReport,
}: {
  view: MatchChatView;
  send: (text: string, phrase: boolean) => Promise<string | null>;
  onReport: (message: ChatMessageView) => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const { me } = useSession();
  const others = view.players.filter((p) => p.username !== me?.username);

  // biome-ignore lint/correctness/useExhaustiveDependencies: stays at the newest message
  useEffect(() => {
    if (list.current) list.current.scrollTop = list.current.scrollHeight;
  }, [view.messages.length]);

  async function say(body: string, phrase: boolean) {
    if (!body.trim() || busy) return;
    setBusy(true);
    setProblem(null);
    const refused = await send(body.trim(), phrase);
    setBusy(false);
    if (refused) setProblem(refused);
    else if (!phrase) setText('');
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2.5 border-b border-divider px-4 py-3">
        <span className="flex" aria-hidden="true">
          {others.slice(0, 3).map((p) => (
            <span key={p.id ?? p.username} className="-ml-2 first:ml-0">
              <Avatar name={p.username} size="xs" ring />
            </span>
          ))}
        </span>
        <div className="min-w-0 flex-1 leading-[18px]">
          <b className="block text-[15px] font-semibold">This match</b>
          <span className="text-[13px] text-muted">
            {others.length === 1 ? '1 other player' : `${others.length} other players`} ·{' '}
            {view.chat === 'typed' ? 'kept 7 days after it ends' : 'quick phrases only'}
          </span>
        </div>
      </div>
      <div
        ref={list}
        role="log"
        aria-label="Match chat"
        className="flex min-h-0 flex-1 flex-col gap-[3px] overflow-y-auto px-4 py-3"
      >
        {view.messages.length === 0 && (
          <p className="mt-auto text-sm text-muted">Say hi, or send a quick phrase.</p>
        )}
        {view.messages.map((m, i) => {
          const runStart = i === 0 || view.messages[i - 1]?.from.id !== m.from.id;
          return (
            <div
              key={m.id}
              className={`group flex max-w-[82%] items-center gap-1 ${m.mine ? 'flex-row-reverse self-end' : 'self-start'} ${runStart && i ? 'mt-2' : ''}`}
            >
              <div className={`flex min-w-0 flex-col ${m.mine ? 'items-end' : 'items-start'}`}>
                {runStart && !m.mine && (
                  <span className="mb-[3px] ml-3 text-xs font-semibold text-muted">
                    {m.from.username}
                  </span>
                )}
                <p
                  className={`m-0 rounded-[18px] px-3.5 py-2 text-[15px] leading-[21px] break-words whitespace-pre-wrap ${
                    m.deleted
                      ? 'border border-dashed border-line text-muted italic'
                      : m.phrase
                        ? m.mine
                          ? 'bg-brand font-display font-bold tracking-[0.02em] text-ink'
                          : 'border border-[#f3dfb5] bg-cream font-display font-bold tracking-[0.02em]'
                        : m.mine
                          ? 'bg-ink text-white'
                          : 'bg-field'
                  }`}
                >
                  {m.deleted ? 'Message deleted' : m.text}
                </p>
              </div>
              {!m.mine && !m.deleted && (
                <button
                  type="button"
                  aria-label={`Report ${m.from.username}’s message`}
                  onClick={() => onReport(m)}
                  className="flex size-8 shrink-0 items-center justify-center rounded-full text-muted opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-field focus:opacity-100"
                >
                  <Flag size={15} strokeWidth={1.9} aria-hidden="true" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {problem && (
        <p
          role="alert"
          className="mx-3 mb-2 flex items-start gap-2 rounded-[14px] bg-[#fbeceb] px-3 py-2 text-sm"
        >
          <CircleAlert
            size={16}
            strokeWidth={1.9}
            aria-hidden="true"
            className="mt-0.5 shrink-0 text-[#c23a32]"
          />
          {problem}
        </p>
      )}
      {view.cantSend ? (
        <p role="status" className="shrink-0 border-t border-divider px-4 py-3 text-sm text-muted">
          {view.cantSend}
        </p>
      ) : (
        <div className="shrink-0 border-t border-divider">
          <div
            role="toolbar"
            aria-label="Quick phrases"
            className="flex flex-wrap gap-1.5 px-3 pt-2.5"
          >
            {view.phrases.map((phrase) => (
              <button
                key={phrase}
                type="button"
                disabled={busy}
                onClick={() => say(phrase, true)}
                className="inline-flex h-9 items-center rounded-full border border-line bg-card px-3.5 text-sm font-semibold hover:bg-field"
              >
                {phrase}
              </button>
            ))}
          </div>
          {view.chat === 'typed' ? (
            <form
              onSubmit={(event: FormEvent) => {
                event.preventDefault();
                void say(text, false);
              }}
              className="flex items-end gap-2 px-3 pt-2.5 pb-3"
            >
              <label htmlFor="match-chat-box" className="sr-only">
                Message this match
              </label>
              <input
                id="match-chat-box"
                value={text}
                maxLength={CHAT_LIMITS.messageLength}
                autoComplete="off"
                placeholder="Message this match"
                onChange={(event) => {
                  setText(event.target.value);
                  if (problem) setProblem(null);
                }}
                className="h-11 min-w-0 flex-1 rounded-full border border-transparent bg-field px-4 text-[15px] focus:border-ink focus:bg-card focus:outline-none"
              />
              <button
                type="submit"
                aria-label="Send"
                disabled={!text.trim() || busy}
                className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand text-ink disabled:bg-field disabled:text-subtle"
              >
                <SendHorizontal size={20} strokeWidth={1.9} aria-hidden="true" />
              </button>
            </form>
          ) : (
            <p className="px-4 pt-2 pb-3 text-[13px] text-muted">Typing isn’t on in this match.</p>
          )}
        </div>
      )}
    </div>
  );
}
